/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { QrCode, HandHelping, Bell, Check, X, DoorOpen, Home, LogOut } from "lucide-react";
import { apiFetch } from "../auth/apiClient";
import { toast } from "../ui";
import VisitorPassPage from "./VisitorPassPage";
import DailyHelpPage from "../dailyhelp/DailyHelpPage";
import "./GatePage.css";

const TABS = [
  { key: "passes", label: "Passes", Icon: QrCode },
  { key: "help", label: "Daily Help", Icon: HandHelping },
  { key: "activity", label: "Activity", Icon: Bell },
];

// The member's single gate feature — pre-approve passes, manage daily help, and
// approve/track who's at the gate. Mirrors MyGate/ADDA's unified gate hub.
export default function GatePage() {
  const [tab, setTab] = useState("passes");
  const [pendingCount, setPendingCount] = useState(0);

  return (
    <div className="vsg-shell">
      <div className="vsg-head">
        <h1>
          <DoorOpen size={22} /> Gate
        </h1>
        <p className="vsg-sub">
          Pre-approve guests, manage your daily help, and approve who's at the
          gate — all in one place.
        </p>
      </div>

      <div className="vsg-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`vsg-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            <t.Icon size={15} /> {t.label}
            {t.key === "activity" && pendingCount > 0 && (
              <span className="vsg-tab-badge">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      <div className="vsg-panel">
        {tab === "passes" && <VisitorPassPage embedded />}
        {tab === "help" && <DailyHelpPage embedded />}
        {tab === "activity" && <ActivityTab onPendingCount={setPendingCount} />}
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
}

const KIND_LABEL = {
  GUEST: "Guest",
  DELIVERY: "Delivery",
  CAB: "Cab",
  SERVICE: "Service",
  OTHER: "Visitor",
};

function statusClass(s) {
  return `vsg-pill ${(s || "").toLowerCase()}`;
}

function ActivityTab({ onPendingCount }) {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      const d = await apiFetch("/api/visitors?villa_id=mine", {
        authRequired: true,
        signal,
      });
      const list = Array.isArray(d) ? d : d.visitors || [];
      setVisitors(list);
      onPendingCount(list.filter((v) => v.status === "PENDING").length);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, [onPendingCount]);

  useEffect(() => {
    const c = new AbortController();
    load(c.signal);
    const id = setInterval(() => load(), 12000);
    return () => {
      c.abort();
      clearInterval(id);
    };
  }, [load]);

  const decide = async (v, decision) => {
    setBusyId(v.id);
    try {
      await apiFetch(`/api/visitors/${v.id}/decision`, {
        method: "POST",
        authRequired: true,
        body: { decision },
      });
      toast.success(decision === "APPROVED" ? `Approved ${v.name}` : `Denied ${v.name}`);
      await load();
    } catch (e) {
      toast.error(e.message || "Could not record your decision");
    } finally {
      setBusyId(null);
    }
  };

  const checkout = async (v) => {
    setBusyId(v.id);
    try {
      await apiFetch(`/api/visitors/${v.id}/check-out`, {
        method: "POST",
        authRequired: true,
      });
      toast.success(`${v.name} checked out.`);
      await load();
    } catch (e) {
      toast.error(e.message || "Could not check out");
    } finally {
      setBusyId(null);
    }
  };

  const pending = visitors.filter((v) => v.status === "PENDING");
  const others = visitors.filter((v) => v.status !== "PENDING");

  if (loading) return <div className="vsg-empty">Loading…</div>;

  return (
    <div className="vsg-activity">
      {pending.length > 0 && (
        <section className="vsg-section">
          <h3>Waiting for your approval</h3>
          {pending.map((v) => (
            <div key={v.id} className="vsg-approve">
              <div className="vsg-approve-main">
                <strong>{v.name}</strong>
                <span className="vsg-approve-sub">
                  {KIND_LABEL[v.kind] || "Visitor"} at the gate
                  {v.purpose ? ` · ${v.purpose}` : ""}
                </span>
              </div>
              <div className="vsg-approve-actions">
                <button
                  className="vsg-btn vsg-btn--deny"
                  disabled={busyId === v.id}
                  onClick={() => decide(v, "DENIED")}
                >
                  <X size={15} /> Deny
                </button>
                <button
                  className="vsg-btn vsg-btn--approve"
                  disabled={busyId === v.id}
                  onClick={() => decide(v, "APPROVED")}
                >
                  <Check size={15} /> Approve
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="vsg-section">
        <h3>Recent at the gate</h3>
        {others.length === 0 ? (
          <div className="vsg-empty">No visitors logged yet.</div>
        ) : (
          <ul className="vsg-log">
            {others.slice(0, 40).map((v) => (
              <li key={v.id}>
                <span className="vsg-log-name">
                  <strong>{v.name}</strong>
                  <span className="vsg-log-meta">
                    {KIND_LABEL[v.kind] || "Visitor"} ·{" "}
                    {fmtTime(v.entered_at || v.created_at)}
                  </span>
                </span>
                <span className="vsg-log-right">
                  <span className={statusClass(v.status)}>{v.status}</span>
                  {v.status === "INSIDE" && (
                    <button
                      className="vsg-btn vsg-btn--checkout"
                      disabled={busyId === v.id}
                      onClick={() => checkout(v)}
                    >
                      <LogOut size={13} /> Check out
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
